import { defaultData } from "./calc";
import { IDB_KEY, IDB_NAME, IDB_STORE, IDB_VERSION, LOCAL_STORAGE_KEY } from "./constants";
import { sanitizeStoredData } from "./sanitize";
import type { Data } from "./types";

// IndexedDB keeps data between sessions in this browser (until browsing data is
// cleared). localStorage is only a fallback for when IndexedDB is unavailable,
// e.g. some private-browsing modes.

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!globalThis.indexedDB) return reject(new Error("no-idb"));
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(IDB_NAME, IDB_VERSION);
    } catch (e) {
      // Some private-browsing modes throw here rather than returning a request.
      return reject(e instanceof Error ? e : new Error("idb-open-failed"));
    }
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(IDB_STORE)) req.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("idb-open-failed"));
  });
}

async function idbGet(key: string): Promise<unknown> {
  const db = await idbOpen();
  try {
    return await new Promise((resolve, reject) => {
      const req = db.transaction(IDB_STORE, "readonly").objectStore(IDB_STORE).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error("idb-get-failed"));
    });
  } finally {
    db.close();
  }
}

async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await idbOpen();
  try {
    return await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("idb-put-failed"));
      tx.onabort = () => reject(tx.error ?? new Error("idb-put-aborted"));
    });
  } finally {
    db.close();
  }
}

function loadFromLocalStorage(): Data | null {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? sanitizeStoredData(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/**
 * Stored state is rebuilt through the same cleaner as an imported file. It was
 * written by this app, but it can still be stale from an older version, damaged,
 * or edited by anything else with access to this origin, and a malformed blob
 * would otherwise crash the app on every load with no way back.
 */
export async function loadData(): Promise<Data> {
  try {
    const saved = sanitizeStoredData(await idbGet(IDB_KEY));
    if (saved) return saved;
  } catch {
    // fall through to localStorage
  }
  return loadFromLocalStorage() ?? defaultData();
}

export async function saveData(data: Data): Promise<void> {
  try {
    await idbSet(IDB_KEY, data);
  } catch {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
    } catch {
      // storage unavailable; the session still works, it just won't persist
    }
  }
}
